// 一个会话里的问答：历史（从服务端载入）+ 正在生成的这一条。
// 回答片段很密，先攒在 ref 里，下一帧再一次性更新界面。

import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { loadMessages, sendMessage, stopMessage, type SendInput } from '../../api/chat'
import type { Message, Reference } from '../../api/types'

/** 回答之前走的一步（灵智原生的工具调用：理解问题、查资料……）。 */
export interface Step {
  name: string
  done: boolean
  ok?: boolean
  /** 不是灵智原生的工具、是网页自己做的一步（缩小范围）时，直接写好的说明。 */
  label?: string
}

export interface Turn {
  id: string
  /** 渲染用的 key：从开始生成到换成服务端历史都不变，元素不会被重建。 */
  key: string
  role: 'user' | 'assistant'
  text: string
  references?: Reference[]
  thinking?: string
  /** 思考了多少毫秒（有思考过程才有）。 */
  thoughtMs?: number
  steps?: Step[]
  streaming?: boolean
  stopped?: boolean
  error?: string
}

const THINK = /^<think>[\s\S]*?<\/think>\s*/

type Extras = Record<string, Pick<Turn, 'thinking' | 'thoughtMs' | 'steps'>>

export function turnsFromMessages(messages: Message[], extras: Extras = {}, keys: Record<string, string> = {}): Turn[] {
  return [...messages]
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => ({
      id: m.id,
      key: keys[m.id] ?? m.id,
      role: m.role as Turn['role'],
      // 历史里的回答可能还带着快速问答的 <think> 段，显示时去掉
      text: m.role === 'assistant' ? (m.content ?? '').replace(THINK, '') : (m.content ?? ''),
      references: m.knowledge_references ?? undefined,
      stopped: m.role === 'assistant' && m.is_completed === false ? true : undefined,
      ...extras[m.id],
    }))
}

export function useChat(sessionId: string) {
  const qc = useQueryClient()
  const history = useQuery({ queryKey: ['messages', sessionId], queryFn: () => loadMessages(sessionId, { limit: 50 }) })
  const [live, setLive] = useState<Turn[]>([])
  const [busy, setBusy] = useState(false)
  // 服务端的历史里不存思考过程和查资料的步骤：按回答的消息 ID 记在这里，换成历史以后照样显示「想了 N 秒」
  const [extras, setExtras] = useState<Extras>({})
  // 服务端消息 ID → 生成时用的 key
  const keys = useRef<Record<string, string>>({})
  const abort = useRef<AbortController | null>(null)
  const assistantId = useRef<string | undefined>(undefined)
  const draft = useRef<Turn | null>(null)
  const frame = useRef<number | null>(null)

  // 换了会话：丢掉上一个会话里正在生成的。React 严格模式会把组件装上、拆下、再装上一次，
  // 那一下不算换会话，也不算离开：只有会话真的变了、或组件真的拆掉了，才中止正在生成的回答。
  const shownSession = useRef(sessionId)
  const mounted = useRef(false)
  useEffect(() => {
    if (shownSession.current !== sessionId) {
      abort.current?.abort()
      shownSession.current = sessionId
      setLive([])
    }
  }, [sessionId])
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      setTimeout(() => {
        if (!mounted.current) abort.current?.abort()
      }, 0)
    }
  }, [])

  const flush = useCallback(() => {
    frame.current = null
    const d = draft.current
    if (!d) return
    setLive((turns) => turns.map((t) => (t.id === d.id ? { ...d } : t)))
  }, [])
  const schedule = useCallback(() => {
    if (frame.current === null) frame.current = requestAnimationFrame(flush)
  }, [flush])

  const send = useCallback(
    async (input: SendInput) => {
      if (busy) return
      const controller = new AbortController()
      abort.current = controller
      assistantId.current = undefined
      const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
      const user: Turn = { id: `u-${stamp}`, key: `u-${stamp}`, role: 'user', text: input.query }
      const answer: Turn = { id: `a-${stamp}`, key: `a-${stamp}`, role: 'assistant', text: '', streaming: true, ...(input.route ? { steps: [{ name: 'route', done: true, label: input.route }] } : {}) }
      draft.current = answer
      setLive((turns) => [...turns, user, answer])
      setBusy(true)
      let thinkStart = 0
      try {
        let req = input
        if (input.prepare) {
          const narrowed = await input.prepare().catch(() => null)
          if (narrowed) {
            req = { ...input, ...narrowed }
            if (narrowed.route) {
              draft.current!.steps = [{ name: 'route', done: true, label: narrowed.route }]
              schedule()
            }
          }
        }
        for await (const e of sendMessage(sessionId, req, controller.signal)) {
          const d = draft.current!
          if (e.kind === 'start') {
            assistantId.current = e.assistantMessageId
            // 换成服务端的消息 ID：回答写完换成历史时是同一个元素，不会重新浮出来一次
            const oldId = d.id
            if (e.assistantMessageId) {
              d.id = e.assistantMessageId
              keys.current[d.id] = d.key
            }
            if (e.userMessageId) keys.current[e.userMessageId] = user.key
            setLive((turns) => turns.map((t) => (t.id === oldId ? { ...d } : t.id === user.id && e.userMessageId ? { ...t, id: e.userMessageId } : t)))
            if (e.userMessageId) user.id = e.userMessageId
          }
          else if (e.kind === 'thinking') {
            thinkStart ||= performance.now()
            d.thinking = (d.thinking ?? '') + e.text
          } else if (e.kind === 'thinking-done') {
            if (thinkStart) d.thoughtMs = performance.now() - thinkStart
          } else if (e.kind === 'answer') {
            if (thinkStart && d.thoughtMs === undefined) d.thoughtMs = performance.now() - thinkStart
            if (d.steps?.some((s) => !s.done)) d.steps = d.steps.map((s) => ({ ...s, done: true }))
            d.text += e.text
          } else if (e.kind === 'tool') {
            const steps = d.steps ?? []
            if (e.phase === 'call') d.steps = [...steps, { name: e.name, done: false }]
            else {
              // 结果对上最近一次同名、还没做完的那一步
              const i = steps.map((s) => !s.done && s.name === e.name).lastIndexOf(true)
              d.steps = i < 0 ? [...steps, { name: e.name, done: true, ok: e.ok }] : steps.map((s, j) => (j === i ? { ...s, done: true, ok: e.ok } : s))
            }
          } else if (e.kind === 'references') d.references = e.references
          else if (e.kind === 'title') qc.invalidateQueries({ queryKey: ['sessions'] })
          else if (e.kind === 'error') d.error = e.message
          schedule()
        }
      } catch (err) {
        const d = draft.current!
        if (controller.signal.aborted) d.stopped = true
        else d.error = err instanceof Error ? err.message : '回答出错了'
      } finally {
        const d = draft.current!
        d.streaming = false
        d.text = d.text.trim()
        if (d.steps?.some((s) => !s.done)) d.steps = d.steps.map((s) => ({ ...s, done: true }))
        if (frame.current !== null) cancelAnimationFrame(frame.current)
        flush()
        draft.current = null
        abort.current = null
        setBusy(false)
        qc.invalidateQueries({ queryKey: ['sessions'] })
        // 顺利写完的一问一答已经存在服务端：重新载入历史后去掉本地这份，免得重复；出错、停下的留着给人看
        const id = assistantId.current
        if (id && (d.thinking || d.steps?.length)) setExtras((x) => ({ ...x, [id]: { thinking: d.thinking, thoughtMs: d.thoughtMs, steps: d.steps } }))
        if (!d.error && !d.stopped) {
          qc.refetchQueries({ queryKey: ['messages', sessionId] }).then(() => setLive((turns) => turns.filter((t) => t.id !== user.id && t.id !== d.id)))
        }
      }
    },
    [busy, sessionId, qc, schedule, flush],
  )

  /** 停止生成：先让服务端停下，再断开连接；已经写出来的留着。 */
  const stop = useCallback(() => {
    const id = assistantId.current
    if (id) stopMessage(sessionId, id).catch(() => undefined)
    abort.current?.abort()
  }, [sessionId])

  const past = turnsFromMessages(history.data ?? [], extras, keys.current)
  const known = new Set(past.map((t) => t.id))
  const turns = [...past, ...live.filter((t) => !known.has(t.id))]
  return { turns, history, busy, send, stop }
}
